import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';

import { apiFetch, ApiError } from '@/api/client';
import { getEngagement } from '@/api/engagements';
import { listEngagementFiles, type EngagementFileOut } from '@/api/engagementFiles';
import { listRuleSets } from '@/api/rule_sets';
import { getMaterialityPreview, listRunsForEngagement, triggerAuditRun } from '@/api/runs';
import { Breadcrumbs } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FileDrop } from '@/components/file-drop';
import { useAuth } from '../auth/AuthContext';
import type { AuditRunSourceType } from '@/api/types';
import { SOURCES_REQUIRING_SUPPLIER_FILE } from '@/api/types';

type PickerMode = 'pool' | 'upload';

interface MeResponse {
  id: string;
  user_type: 'practitioner' | 'customer';
}

/** /engagements/$id/runs/new — dedicated upload page.
 *
 * Lifted out of the engagement detail page so the practitioner has a
 * clean focus surface when starting a run; the detail page now just
 * lists existing runs and links here for a new one.
 */
export function NewRunPage() {
  const { id } = useParams({ from: '/engagements/$id/runs/new' });
  const { isSignedIn, userKey, getIdToken } = useAuth();

  const me = useQuery({
    queryKey: ['me', userKey],
    enabled: isSignedIn,
    queryFn: async () => apiFetch<MeResponse>('/api/me', { idToken: (await getIdToken() ?? '') }),
  });

  const engagement = useQuery({
    queryKey: ['engagements', id, userKey],
    enabled: isSignedIn,
    queryFn: async () => getEngagement(id, (await getIdToken() ?? '')),
  });

  if (!isSignedIn) {
    return (
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Not signed in</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  if (engagement.isPending || me.isPending) {
    return <p className="text-brand-slate">Loading…</p>;
  }
  if (engagement.isError) {
    console.error('engagement load failed:', engagement.error);
    return (
      <Card className="max-w-xl border-destructive/30">
        <CardHeader>
          <CardTitle>Engagement not found</CardTitle>
          <CardDescription>Try refreshing. Details in the browser console.</CardDescription>
        </CardHeader>
      </Card>
    );
  }
  if (me.isError) {
    console.error('me load failed:', me.error);
    return (
      <Card className="max-w-xl border-destructive/30">
        <CardHeader>
          <CardTitle>Couldn't verify access</CardTitle>
          <CardDescription>Try refreshing. Details in the browser console.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const e = engagement.data;
  // Practitioners only. Defense-in-depth — the API enforces this too
  // (POST /api/engagements/{id}/runs is practitioner-only), but bouncing
  // a customer here avoids rendering the upload form just to 403. The
  // gate sits AFTER the me.isPending / me.isError guards above so the
  // form never renders while the role is still unknown.
  if (me.data.user_type !== 'practitioner') {
    return (
      <Card className="max-w-xl border-destructive/30">
        <CardHeader>
          <CardTitle>Practitioners only</CardTitle>
          <CardDescription>
            Customers can review approved findings but can't kick off a new run.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="grid gap-6">
      <header>
        <Breadcrumbs>
          <Link to="/customers" className="text-brand-royal underline-offset-4 hover:underline">
            Customers
          </Link>
          <Link
            to="/customers/$slug"
            params={{ slug: e.customer_slug }}
            className="text-brand-royal underline-offset-4 hover:underline"
          >
            {e.customer_name}
          </Link>
          <Link
            to="/engagements/$id"
            params={{ id: e.id }}
            className="text-brand-royal underline-offset-4 hover:underline"
          >
            {e.name}
          </Link>
          <span className="text-brand-navy">New run</span>
        </Breadcrumbs>
        <h2 className="mt-2 text-2xl font-semibold tracking-tight text-brand-navy">New audit run</h2>
        <p className="mt-1 text-sm text-brand-slate">
          Upload the source files for this engagement period. The audit kicks off as soon as
          you submit; results appear back on the engagement page when it finishes.
        </p>
      </header>
      <NewRunForm engagementId={e.id} />
    </div>
  );
}

const _MAX_UPLOAD_BYTES = 25 * 1024 * 1024; // mirrors server-py routes/audit_runs._MAX_UPLOAD_BYTES

// Pre-selected when the rule-set list contains it — covers everything
// shipped today and is what a new engagement should reach for first.
const _DEFAULT_RULE_SET_ID = 'all-rules';

// De-minimis slider bounds (INP-366). Mirrors the server's preview
// curve, which is computed for integer floors $1..$100.
const _DEFAULT_MATERIALITY_FLOOR = 1;
const _MAX_MATERIALITY_FLOOR = 100;

function NewRunForm({ engagementId }: { engagementId: string }) {
  const { isSignedIn, userKey, getIdToken } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<File | null>(null);
  const [suppliers, setSuppliers] = useState<File | null>(null);
  const [transactionsFileId, setTransactionsFileId] = useState<string>('');
  const [suppliersFileId, setSuppliersFileId] = useState<string>('');
  const [transactionsMode, setTransactionsMode] = useState<PickerMode>('upload');
  const [suppliersMode, setSuppliersMode] = useState<PickerMode>('upload');
  const [sourceType, setSourceType] = useState<AuditRunSourceType>('qbo');
  const [ruleSetId, setRuleSetId] = useState<string>(_DEFAULT_RULE_SET_ID);
  const [asOf, setAsOf] = useState('');
  const [carryForward, setCarryForward] = useState(false);
  // Empty string = "let the server pick (published > most recent completed)".
  // A specific run-id pins the source.
  const [carryFrom, setCarryFrom] = useState<string>('');
  // De-minimis floor in whole CAD dollars (INP-366). Slider bottoms out
  // at $1 — the API accepts 0 to disable the sweep, but the form always
  // applies at least the $1 default.
  const [materialityFloor, setMaterialityFloor] = useState(_DEFAULT_MATERIALITY_FLOOR);

  const needsSuppliers = SOURCES_REQUIRING_SUPPLIER_FILE.has(sourceType);

  const ruleSets = useQuery({
    queryKey: ['rule-sets', userKey],
    enabled: isSignedIn,
    queryFn: async () => listRuleSets((await getIdToken() ?? '')),
  });

  const pool = useQuery({
    queryKey: ['engagement-files', engagementId, userKey],
    enabled: isSignedIn,
    queryFn: async () => listEngagementFiles(engagementId, (await getIdToken()) ?? ''),
  });

  // Prior runs to populate the carry-forward source dropdown. Filtered
  // client-side to completed + non-archived; ordered newest-first by
  // the API. Archived runs would still be valid carry-forward sources
  // (the disposition signal lives on findings, not the run row) but
  // they're hidden here so the dropdown stays focused on the active set;
  // the server-side resolver can still pick them by explicit pin.
  const priorRuns = useQuery({
    queryKey: ['runs', 'by-engagement', engagementId, userKey],
    enabled: isSignedIn,
    queryFn: async () => listRunsForEngagement(engagementId, (await getIdToken()) ?? ''),
  });
  const carryEligibleRuns = useMemo(
    () => (priorRuns.data?.items ?? []).filter((r) => r.status === 'completed'),
    [priorRuns.data],
  );

  // Whole $1..$100 sweep curve in one fetch — slider drags read from it
  // locally. Empty points = no completed run to preview from (first-ever
  // run); the preview line just doesn't render.
  const materialityPreview = useQuery({
    queryKey: ['materiality-preview', engagementId, userKey],
    enabled: isSignedIn,
    queryFn: async () => getMaterialityPreview(engagementId, (await getIdToken()) ?? ''),
  });
  const previewPoint = useMemo(
    () =>
      (materialityPreview.data?.points ?? []).find(
        (p) => Math.round(Number(p.floor)) === materialityFloor,
      ) ?? null,
    [materialityPreview.data, materialityFloor],
  );

  // Pool files split by kind, newest first — practitioner usually wants
  // the most recently uploaded file in each slot.
  const transactionsPool = useMemo(
    () => sortPoolByNewest(pool.data?.items, 'transactions'),
    [pool.data],
  );
  const suppliersPool = useMemo(
    () => sortPoolByNewest(pool.data?.items, 'suppliers'),
    [pool.data],
  );

  // When pool files exist, default to picker mode and preselect the
  // newest. The init refs gate this to a one-shot per kind so a user
  // toggle back to "Upload new" isn't immediately undone by the next
  // render (CodeRabbit on #281 caught this — the old guard checked
  // `mode === 'upload'`, which is exactly the post-toggle state, so
  // the effect would flip it right back).
  const transactionsPoolInitialized = useRef(false);
  const suppliersPoolInitialized = useRef(false);
  useEffect(() => {
    if (transactionsPool.length > 0 && !transactionsPoolInitialized.current) {
      transactionsPoolInitialized.current = true;
      setTransactionsMode('pool');
      setTransactionsFileId(transactionsPool[0].id);
    }
  }, [transactionsPool]);
  useEffect(() => {
    if (
      needsSuppliers &&
      suppliersPool.length > 0 &&
      !suppliersPoolInitialized.current
    ) {
      suppliersPoolInitialized.current = true;
      setSuppliersMode('pool');
      setSuppliersFileId(suppliersPool[0].id);
    }
  }, [suppliersPool, needsSuppliers]);

  const trigger = useMutation({
    mutationFn: async () =>
      triggerAuditRun(
        engagementId,
        {
          transactions: transactionsMode === 'upload' ? transactions : null,
          transactions_file_id: transactionsMode === 'pool' ? transactionsFileId : null,
          suppliers: needsSuppliers && suppliersMode === 'upload' ? suppliers : null,
          suppliers_file_id:
            needsSuppliers && suppliersMode === 'pool' ? suppliersFileId : null,
          source_type: sourceType,
          rule_set_id: ruleSetId,
          as_of: asOf || undefined,
          carry_forward: carryForward,
          carry_forward_from: carryForward && carryFrom ? carryFrom : null,
          materiality_floor: materialityFloor,
        },
        (await getIdToken() ?? ''),
      ),
    onSuccess: (run) => {
      qc.invalidateQueries({ queryKey: ['runs', 'by-engagement', engagementId, userKey] });
      void navigate({ to: '/runs/$id', params: { id: run.id } });
    },
  });

  const transactionsReady =
    transactionsMode === 'upload' ? transactions !== null : transactionsFileId !== '';
  const suppliersReady = needsSuppliers
    ? suppliersMode === 'upload'
      ? suppliers !== null
      : suppliersFileId !== ''
    : true;
  const submittable = transactionsReady && suppliersReady && !trigger.isPending;

  return (
    <Card className="w-full max-w-2xl">
      <CardContent className="pt-6">
        <form
          className="grid gap-4"
          onSubmit={(ev) => {
            ev.preventDefault();
            if (!submittable) return;
            trigger.mutate();
          }}
        >
          <label className="grid gap-1 text-sm">
            <span className="text-brand-slate">Accounting source</span>
            <select
              value={sourceType}
              onChange={(ev) => setSourceType(ev.target.value as AuditRunSourceType)}
              className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
            >
              <option value="qbo">QuickBooks Online</option>
              <option value="netsuite">Netsuite (GL export)</option>
              <option value="xero">Xero (merged canonical CSV)</option>
              <option value="sage" disabled>
                Sage (coming soon)
              </option>
            </select>
          </label>

          <label className="grid gap-1 text-sm">
            <span className="text-brand-slate">
              Rule set{' '}
              <Link
                to="/rule-sets"
                className="text-xs text-brand-royal underline-offset-4 hover:underline"
              >
                (browse)
              </Link>
            </span>
            <select
              value={ruleSetId}
              onChange={(ev) => setRuleSetId(ev.target.value)}
              disabled={ruleSets.isPending}
              className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal disabled:opacity-50"
            >
              {ruleSets.isPending ? (
                <option value={ruleSetId}>Loading…</option>
              ) : ruleSets.isError ? (
                <option value={ruleSetId}>Couldn't load — using {ruleSetId}</option>
              ) : (
                ruleSets.data.items.map((rs) => (
                  <option key={rs.id} value={rs.id}>
                    {rs.id} (v{rs.version}, {rs.rules.length}{' '}
                    {rs.rules.length === 1 ? 'rule' : 'rules'})
                  </option>
                ))
              )}
            </select>
          </label>

          <PoolOrUploadSlot
            label={
              sourceType === 'netsuite' ? 'Netsuite GL export' : 'Transaction Detail'
            }
            mode={transactionsMode}
            onModeChange={setTransactionsMode}
            pool={transactionsPool}
            fileId={transactionsFileId}
            onFileIdChange={setTransactionsFileId}
            uploadValue={transactions}
            onUploadChange={setTransactions}
          />

          {needsSuppliers && (
            <PoolOrUploadSlot
              label="Supplier Contact List"
              mode={suppliersMode}
              onModeChange={setSuppliersMode}
              pool={suppliersPool}
              fileId={suppliersFileId}
              onFileIdChange={setSuppliersFileId}
              uploadValue={suppliers}
              onUploadChange={setSuppliers}
            />
          )}

          <label className="grid gap-1 text-sm">
            <span className="text-brand-slate">
              As-of date <span className="text-xs">(optional — defaults to today)</span>
            </span>
            <input
              type="date"
              value={asOf}
              onChange={(ev) => setAsOf(ev.target.value)}
              className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
            />
          </label>

          <div className="grid gap-2 rounded-md border border-input p-3">
            <label className="grid gap-1 text-sm">
              <span className="flex items-baseline justify-between">
                <span className="font-medium text-brand-navy">Materiality floor</span>
                <span className="tabular-nums text-brand-navy">
                  ${materialityFloor} CAD
                </span>
              </span>
              <span className="text-xs text-brand-slate">
                Recovery findings under this amount are auto-rejected as immaterial
                (reason code <code>immaterial_amount</code>). Each one stays visible in
                the rejected bucket and can be unreviewed individually.
              </span>
              <input
                type="range"
                min={_DEFAULT_MATERIALITY_FLOOR}
                max={_MAX_MATERIALITY_FLOOR}
                step={1}
                value={materialityFloor}
                onChange={(ev) => setMaterialityFloor(Number(ev.target.value))}
                className="accent-brand-royal"
              />
            </label>
            {previewPoint && (
              <p className="text-xs text-brand-slate">
                Auto-rejects{' '}
                <span className="font-medium text-brand-navy">
                  {previewPoint.finding_count}{' '}
                  {previewPoint.finding_count === 1 ? 'finding' : 'findings'}
                </span>{' '}
                worth{' '}
                <span className="font-medium text-brand-navy">
                  {formatCad(Number(previewPoint.recovery_total))}
                </span>{' '}
                of potential recovery (based on the latest completed run).
              </p>
            )}
          </div>

          <div className="grid gap-2 rounded-md border border-input p-3">
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={carryForward}
                onChange={(ev) => setCarryForward(ev.target.checked)}
                disabled={priorRuns.isPending || carryEligibleRuns.length === 0}
                className="mt-0.5"
              />
              <span>
                <span className="font-medium text-brand-navy">
                  Carry forward dispositions from a previous run
                </span>
                <span className="block text-xs text-brand-slate">
                  Approved / rejected / needs-review findings matched by transaction id
                  inherit their state automatically. Practitioner overrides clear the carry
                  flag.
                </span>
                {!priorRuns.isPending && carryEligibleRuns.length === 0 && (
                  <span className="mt-1 block text-xs text-brand-slate">
                    No completed prior runs on this engagement yet — carry-forward is
                    disabled.
                  </span>
                )}
              </span>
            </label>
            {carryForward && carryEligibleRuns.length > 0 && (
              <label className="grid gap-1 pl-6 text-xs">
                <span className="text-brand-slate">Source run</span>
                <select
                  value={carryFrom}
                  onChange={(ev) => setCarryFrom(ev.target.value)}
                  className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
                >
                  <option value="">
                    Latest published run (server picks)
                  </option>
                  {carryEligibleRuns.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.rule_set_id} v{r.rule_set_version} —{' '}
                      {new Date(r.started_at).toLocaleDateString()} · {r.finding_count}{' '}
                      findings
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button type="submit" size="sm" disabled={!submittable}>
              {trigger.isPending ? 'Starting…' : 'Start run'}
            </Button>
            <Link
              to="/engagements/$id"
              params={{ id: engagementId }}
              className="text-sm text-brand-slate underline-offset-4 hover:underline"
            >
              Cancel
            </Link>
            {trigger.isError && (
              <span className="text-xs text-destructive">
                {trigger.error instanceof ApiError
                  ? formatRunError(trigger.error)
                  : 'Start failed — see console.'}
              </span>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function sortPoolByNewest(
  items: EngagementFileOut[] | undefined,
  kind: 'transactions' | 'suppliers',
): EngagementFileOut[] {
  if (!items) return [];
  return items
    .filter((f) => f.kind === kind)
    .slice()
    .sort((a, b) => b.uploaded_at.localeCompare(a.uploaded_at));
}

function PoolOrUploadSlot({
  label,
  mode,
  onModeChange,
  pool,
  fileId,
  onFileIdChange,
  uploadValue,
  onUploadChange,
}: {
  label: string;
  mode: PickerMode;
  onModeChange: (m: PickerMode) => void;
  pool: EngagementFileOut[];
  fileId: string;
  onFileIdChange: (id: string) => void;
  uploadValue: File | null;
  onUploadChange: (f: File | null) => void;
}) {
  const hasPool = pool.length > 0;
  return (
    <div className="grid gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm text-brand-slate">{label}</span>
        {hasPool && (
          <div className="text-xs">
            <button
              type="button"
              onClick={() => onModeChange('pool')}
              className={
                mode === 'pool'
                  ? 'font-medium text-brand-navy'
                  : 'text-brand-royal underline-offset-4 hover:underline'
              }
            >
              Pick from pool
            </button>
            <span className="mx-1 text-brand-slate">·</span>
            <button
              type="button"
              onClick={() => onModeChange('upload')}
              className={
                mode === 'upload'
                  ? 'font-medium text-brand-navy'
                  : 'text-brand-royal underline-offset-4 hover:underline'
              }
            >
              Upload new
            </button>
          </div>
        )}
      </div>

      {mode === 'pool' && hasPool ? (
        <select
          value={fileId}
          onChange={(ev) => onFileIdChange(ev.target.value)}
          className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
          required
        >
          <option value="" disabled>
            Choose an existing file…
          </option>
          {pool.map((f) => (
            <option key={f.id} value={f.id}>
              {f.filename} ({formatBytes(f.byte_count)}, uploaded {formatRelative(f.uploaded_at)})
            </option>
          ))}
        </select>
      ) : (
        <FileDrop
          label=""
          hint=".xlsx or .csv"
          accept=".xlsx,.csv"
          value={uploadValue}
          onChange={onUploadChange}
          required
          maxBytes={_MAX_UPLOAD_BYTES}
        />
      )}
    </div>
  );
}

function formatCad(n: number): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 2,
  }).format(n);
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diff = Date.now() - then;
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function formatRunError(err: ApiError): string {
  if (err.status === 413) return 'File too large (25 MB limit).';
  if (
    err.body &&
    typeof err.body === 'object' &&
    'detail' in err.body &&
    typeof (err.body as { detail: unknown }).detail === 'string'
  ) {
    return (err.body as { detail: string }).detail;
  }
  return `Start failed (HTTP ${err.status}) — see console.`;
}
